











































































import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { AxiosError } from 'axios';
import { deploySmartContract, mintOperator } from '@/api/SuperAdmin';
import { errorToastMessage } from '@/helpers/errorToastMessage';
import { Status } from '@/helpers/operatorHelpers';

import type {
  SuperAdminGameProviderOperator,
  AccountOperatorItem
} from '@/api/schema';
import { SuperAdminMenuLink } from '@/api/schema';
import OperatorDropdown from '@/components/operator/OperatorDropdown.vue';
import {
  ECopyStakeFeatureFlag,
  ELoyaltyFeatureFlag
} from '@/store/modules/feature-flags/FeatureFlagNames';

const SuperAdminCreateOperatorDialog = (): any =>
  import(
    './SuperAdminCreateOperatorDialog/SuperAdminCreateOperatorDialog.vue'
  );

const SuperAdminOperatorViewMenu = (): Promise<any> =>
  import(
    '@/views/SuperAdminViews/super-admin-operator-view-menu/SuperAdminOperatorViewMenu.vue'
  );

const ConfirmationPromiseDialog = (): any =>
  import('@/components/ConfirmationPromiseDialog.vue');

export default {
  name: 'SuperAdminOperatorView',
  components: {
    OperatorDropdown,
    SuperAdminOperatorViewMenu,
    SuperAdminCreateOperatorDialog,
    ConfirmationPromiseDialog
  },
  data: (): any => ({
    Status,
    openCreateAccountDialog: false,
    isLoading: {
      mint: false,
      deploy: false
    }
  }),
  computed: {
    ...mapState('SuperAdmin', {
      operatorId: 'operatorId',
      account: 'currentAccount'
    }),
    ...mapGetters('FeatureFlags', [
      'isAdminVisibleFeatureFlag',
      'isEnabledFeatureFlag'
    ]),
    ...mapGetters('SuperAdmin', ['currentOperator', 'sortedOperators']),
    ...mapState('Operator', ['loading']),
    ...mapState('Auth', ['tokenSymbol']),

    menu(): SuperAdminMenuLink[] {
      const menuItems = [
        {
          title: 'Operator Balance',
          icon: 'mdi-cash',
          routeName: 'account-operator-balance'
        },
        {
          icon: 'mdi-chart-box-outline',
          title: 'Home',
          routeName: 'account-operator-home'
        },
        {
          icon: 'mdi-bell-cog-outline',
          title: 'Loyalty Promotion',
          routeName: 'account-operator-loyalty-promotion'
        },
        {
          icon: 'mdi-view-dashboard-outline',
          title: 'Dashboard',
          routeName: 'account-operator-dashboard'
        },

        {
          title: 'Programs',
          icon: 'mdi-file-cog-outline',
          routeName: 'account-operator-programs',
          children: [
            {
              title: 'Play to Earn',
              routeName: 'account-operator-programs-p2e'
            },
            {
              title: 'Hold to Earn',
              routeName: 'account-operator-programs-h2e'
            },
            {
              title: 'Marketing Campaigns',
              routeName: 'account-operator-programs-promo'
            },
            {
              title: 'Daily Check-In',
              routeName: 'account-operator-daily-check-in'
            }
          ]
        },
        {
          title: 'Missions & Rewards',
          icon: 'mdi-gift-outline',
          routeName: '',
          visible: this.isEnabledFeatureFlag(
            ELoyaltyFeatureFlag.MISSIONS_AND_REWARDS
          ),
          children: [
            {
              title: 'Missions',
              routeName: 'account-operator-missions'
            },
            {
              title: 'Rewards',
              routeName: 'account-operator-rewards'
            }
          ]
        },
        {
          title: 'Token',
          icon: 'mdi-circle-multiple-outline',
          routeName: 'account-operator-token',
          children: [
            {
              title: 'Users',
              routeName: 'account-operator-token-users'
            },
            {
              title: 'Token Management',
              routeName: 'account-operator-token-management'
            },
            {
              title: 'Payment Limits',
              routeName: 'account-operator-token-payment-limits'
            },
            {
              title: 'Withdrawal Requests',
              routeName: 'account-operator-token-withdraw-requests'
            },
            {
              title: 'Token Sale',
              routeName: 'account-operator-token-sale'
            },
            {
              title: 'Stats',
              routeName: 'account-operator-token-stats'
            }
          ]
        },
        {
          title: 'CopyStake',
          icon: 'mdi-podcast',
          routeName: 'account-operator-copy-stake',
          visible: this.$role.can.read('copy_stake streamers'),
          children: [
            {
              title: 'Guidance',
              routeName: 'account-operator-copy-stake-guidance'
            },
            {
              title: 'Streamers',
              routeName: 'account-operator-copy-stake-streamers'
            },
            {
              title: 'Become a Streamer',
              routeName: 'account-operator-copy-stake-web-streamers',
              visible: this.isAdminVisibleFeatureFlag(
                ECopyStakeFeatureFlag.WEB_STREAMERS
              )
            },
            {
              title: 'Dashboard',
              routeName: 'account-operator-copy-stake-dashboard'
            },
            {
              title: 'Stats',
              routeName: 'account-operator-copy-stake-stats'
            },
            {
              title: 'Streaming Schedule',
              routeName: 'account-operator-copy-stake-streaming-schedule'
            },
            {
              title: 'Game Providers Report',
              routeName: 'account-operator-copy-stake-game-providers'
            },
            {
              title: 'Games Whitelist',
              routeName: 'account-operator-copy-stake-game-whitelist'
            },
            {
              title: 'Players Blacklist',
              routeName: 'account-operator-copy-stake-user-blacklist'
            },
            {
              title: 'Gamification Center',
              routeName: 'account-operator-copy-stake-gamification-center',
              visible:
                this.isAdminVisibleFeatureFlag(
                  ECopyStakeFeatureFlag.GAMIFICATION_CENTER
                ) && this.$role.can.read('copy_stake gamification_center')
            },
            {
              title: 'Fraud Reports',
              routeName: 'account-operator-copy-stake-fraud-reports'
            },
            {
              title: 'Autoclicker',
              routeName: 'account-operator-copy-stake-autoclicker',
              visible: this.isEnabledFeatureFlag(
                ECopyStakeFeatureFlag.COPYSTAKE_AUTOCLICKER
              )
            }
          ]
        },
        {
          icon: 'mdi-cog-outline',
          title: 'Settings',
          routeName: 'account-operator-settings'
        }
      ];

      return menuItems
        .filter(this.showMenuItem)
        .map((menuItem: SuperAdminMenuLink) => {
          if (!menuItem.children) return menuItem;

          return {
            ...menuItem,
            children: menuItem.children.filter(this.showMenuItem)
          };
        });
    },

    showMintButton(): boolean {
      return (
        !this.currentOperator?.minted &&
        !this.currentOperator?.deployed &&
        !this.currentOperator?.test &&
        this.$role.can.update('super_admin_mint')
      );
    },

    showDeployButton(): boolean {
      return (
        this.currentOperator?.minted &&
        !this.currentOperator?.deployed &&
        !this.currentOperator?.test &&
        this.$role.can.read('super_admin_deploy')
      );
    },

    showWidgetButtons(): boolean {
      return this.currentOperator?.status === Status.on;
    },

    isWidgetEnabled(): boolean {
      return this.currentOperator?.widgetEnabled;
    },

    currentOperatorId(): number {
      return +this.$route.params.operatorId;
    }
  },

  async created(): Promise<void> {
    await this.loadAccount();
    this.setOperatorId(this.currentOperatorId);
  },

  watch: {
    async currentOperator(operator: AccountOperatorItem): Promise<void> {
      if (!operator) return;

      this.setSelectedOperator(operator);
      await this.getOperatorDetailsById(operator.id);
    },

    operatorId(id: boolean): void {
      if (!id) return;

      if (!this.currentOperator || !this.currentOperatorId) {
        this.$router.push({ name: 'accounts' });
      }
    },

    operator(): void {
      this.getApiKeys();
    }
  },
  methods: {
    ...mapActions('SuperAdmin', [
      'getOperatorDetailsById',
      'getAccountDetailsById'
    ]),
    ...mapActions('Onboarding', ['getApiKeys']),
    ...mapActions('Operator', ['updateOperatorWidgetStatus']),
    ...mapMutations('SuperAdmin', ['setOperatorId', 'setAccount']),
    ...mapMutations('Onboarding', ['setSelectedOperator']),

    showMenuItem(link: SuperAdminMenuLink): boolean {
      return link?.visible !== false;
    },

    async mintHandler(): Promise<void> {
      this.isLoading.mint = true;
      const confirmData = await this.$refs.confirm.openDialog({
        title: `Mint`,
        text: `Are you sure yo want to mint “${this.tokenSymbol}“ in database?`,
        controls: {
          submit: {
            color: 'primary',
            text: 'Confirm'
          }
        }
      });

      if (!confirmData) {
        this.isLoading.mint = false;

        return;
      }

      mintOperator(this.currentOperator.id)
        .then(() => {
          this.$toast.success('Mint in database is successful');
        })
        .catch((e: AxiosError) => {
          errorToastMessage(e);
        })
        .finally(async () => {
          await this.loadAccount();
          this.isLoading.mint = false;
        });
    },
    async deployHandler(): Promise<void> {
      this.isLoading.deploy = true;

      const confirmData = await this.$refs.confirm.openDialog({
        title: `Deploy`,
        text: `Are you sure yo want to deploy “${this.tokenSymbol}“ in blockchain?`,
        controls: {
          submit: {
            color: 'primary',
            text: 'Confirm'
          }
        }
      });

      if (!confirmData) {
        this.isLoading.deploy = false;

        return;
      }

      deploySmartContract(this.currentOperator.id)
        .then(() => {
          this.$toast.success('Smart contract has deployed');
        })
        .catch((e: AxiosError) => {
          errorToastMessage(e);
        })
        .finally(async () => {
          await this.loadAccount();
          this.isLoading.deploy = false;
        });
    },

    selectOperator(operator: SuperAdminGameProviderOperator): void {
      this.setOperatorId(operator.id);

      if (this.currentOperatorId === operator.id) return;

      this.$router.replace({ params: { operatorId: operator.id } });
    },

    addAccount(): void {
      this.openCreateAccountDialog = true;
    },

    async enableWidget(value: boolean): Promise<void> {
      if (!this.currentOperator) return;

      await this.updateOperatorWidgetStatus({
        operatorId: this.currentOperator.id,
        enabled: value
      });

      await this.loadAccount();
    },

    async loadAccount(): Promise<void> {
      await this.getAccountDetailsById(this.$route.params.id);
    }
  },

  beforeDestroy(): void {
    this.setAccount(null);
    this.setOperatorId(null);
  }
};
